import { datadogLogs } from "@datadog/browser-logs";
import { useEffect, useMemo, useState } from "react";

import { assertNativeError, delay } from "@examspark/common";
import { GoogleClassroomAssignment } from "@examspark/types";

import { useScript } from "./useScript";

const ddlog = datadogLogs.logger;

export const useGoogleClassroom = (config: { tokenServerUrl: string }) => {
  const gapiScript = useScript("https://apis.google.com/js/api.js");
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (gapiScript === "error") {
      console.error("Failed to load Google Classroom API <script/>");
      ddlog.error("Failed to load Google Classroom API <script/>");
      setError("Failed to load Google Classroom API");
    }
    if (gapiScript !== "ready") return;

    console.log("ready!");
    gapi.load("client", {
      callback: () => {
        const initPromise = gapi.client
          .init({
            discoveryDocs: [
              "https://classroom.googleapis.com/$discovery/rest?version=v1",
            ],
          })
          .then(() => {
            setInitialized(true);
            console.info("Google Classroom API initialized successfully");
            return true;
          });

        // The `.init()` does not reject if there is a network error,
        // so we add a max timeout
        const race = delay(10_000).then(() => false);
        Promise.race([initPromise, race])
          .then((result) => {
            if (result === false) {
              throw new Error(`Timeout initializing Google Classroom API`);
            }
          })
          .catch((e) => {
            console.error(`Failed to initialize Google Classroom API`, e);
            ddlog.error(`Failed to initialize Google Classroom API`, {
              error: e,
            });
            setError("Failed to initialize Google Classroom API");
          });
      },
    });
  }, [gapiScript]);

  return useMemo(() => {
    const setToken = (token: string) => {
      ddlog.info("Google Classroom token updated", { token });
      gapi.client.setToken({ access_token: token });
    };

    const fetchCourses = async () => {
      if (!initialized) {
        console.error("Google classroom JS API is not initialized yet");
        ddlog.error("Google classroom JS API is not initialized yet");
        return;
      }

      try {
        const response = await gapi.client.classroom.courses.list({
          courseStates: ["ACTIVE"],
          teacherId: "me",
        });

        // console.log(JSON.stringify(response.result, null, 2));
        return response.result.courses;
      } catch (error: any) {
        console.log(
          `Unable to fetch courses. Reason: ${error.result.error.message} (${error.result.error.status})`,
        );
      }
    };

    const fetchStudents = async (courseId: string) => {
      if (!initialized) {
        console.error("Google classroom JS API is not initialized yet");
        ddlog.error("Google classroom JS API is not initialized yet");
        return;
      }
      const response = await gapi.client.classroom.courses.students.list({
        courseId,
      });

      // console.log(JSON.stringify(response.result, null, 2));
      return response.result;
    };

    const getStudent = async (courseId: string, userId: string) => {
      if (!initialized) {
        console.error("Google classroom JS API is not initialized yet");
        ddlog.error("Google classroom JS API is not initialized yet");
        return;
      }
      ddlog.info("Fetching student data", {
        courseId,
        userId,
      });
      console.log("Fetching student data", {
        courseId,
        userId,
      });

      try {
        const response = await Promise.race([
          gapi.client.classroom.courses.students.get({
            courseId,
            userId,
          }),
          new Promise<false>((resolve) =>
            setTimeout(() => resolve(false), 30000),
          ),
        ]);

        if (response === false) {
          throw new Error(`Timeout attempting to contact Google Classroom API`);
        }

        console.log(
          "classroom student",
          JSON.stringify(response.result, null, 2),
        );
        ddlog.info("Google Classroom: Student Record Found", {
          result: response,
        });
        return response.result;
      } catch (error) {
        ddlog.info("Google Classroom: Student Record Could not be Found", {
          error,
        });
        console.info("Student record could not be found");
      }
    };

    const listCourseWorkSubmissions = async (
      courseId: string,
      courseWorkId: string,
    ) => {
      try {
        ddlog.info("Listing student submissions for coursework", {
          courseId,
          courseWorkId,
        });
        const result =
          await gapi.client.classroom.courses.courseWork.studentSubmissions.list(
            {
              courseId,
              courseWorkId,
            },
          );
        return result;
      } catch (error: any) {
        throw new Error(
          `Failed to return student submissions. ${error?.result?.error.message}`,
        );
      }
    };

    const getCourseWorkUserSubmission = async (
      courseId: string,
      courseWorkId: string,
      userId: string,
    ) => {
      console.log("getting student submissions", {
        courseId,
        courseWorkId,
        userId,
      });
      const result =
        await gapi.client.classroom.courses.courseWork.studentSubmissions.list({
          courseId,
          courseWorkId,
          userId,
        });

      console.log("submission result", result.result.studentSubmissions);
      return result.result.studentSubmissions?.[0];
    };

    const turnInCourseWork = async (
      courseId: string,
      courseWorkId: string,
      submissionId: string,
      url: string,
    ) => {
      // attempting to submit the same attachement twice will result in an error
      const existingAttachment =
        await gapi.client.classroom.courses.courseWork.studentSubmissions.get({
          courseId,
          courseWorkId,
          id: submissionId,
        });

      let attachResultToSubmissionResult = null;
      if (
        !existingAttachment.result.assignmentSubmission?.attachments?.length
      ) {
        // already attached from a previous submission
        // this behavior may change (multiple attachments)
        // once attempts are working
        attachResultToSubmissionResult =
          await gapi.client.classroom.courses.courseWork.studentSubmissions.modifyAttachments(
            {
              courseId,
              courseWorkId,
              id: submissionId,
              resource: {
                addAttachments: [
                  {
                    link: {
                      url,
                    },
                  },
                ],
              },
            },
          );
      }

      const turnInResult =
        await gapi.client.classroom.courses.courseWork.studentSubmissions.turnIn(
          {
            courseId,
            courseWorkId,
            id: submissionId,
            resource: {},
          },
        );

      return { attachResultToSubmissionResult, turnInResult };
    };

    const createCourseWork = async (courseId: string, data: any) => {
      if (!initialized) {
        console.error("Google classroom JS API is not initialized yet");
        ddlog.error("Google classroom JS API is not initialized yet");
        return;
      }
      console.log("trying to create coursework");
      try {
        const response = await gapi.client.classroom.courses.courseWork.create(
          {
            courseId,
          },
          {
            courseId,
            title: data.title,
            description: data.description,
            workType: "ASSIGNMENT",
            state: "PUBLISHED",
            maxPoints: 100,
            materials: [
              {
                link: {
                  title: "ExamSpark Activity",
                  thumbnailUrl:
                    "https://examspark-app-files-master.s3.amazonaws.com/public/examspark-logo-raster.png",
                  url: `${config.tokenServerUrl}/launch/exam/${data.examConfigId}?from=gc`,
                },
              },
            ],
          },
        );

        return response.result;
      } catch (error: any) {
        throw new Error(error?.result?.error?.message);
      }
    };

    const deleteCourseWork = async (courseId: string, courseWorkId: string) => {
      const response = await gapi.client.classroom.courses.courseWork.delete({
        courseId,
        id: courseWorkId,
      });

      return response.result;
    };

    const findUserSubmissionsInExamAssignments = async (
      userId: string,
      courses: GoogleClassroomAssignment[],
    ) => {
      const submissions: (GoogleClassroomAssignment & {
        submission: gapi.client.classroom.StudentSubmission;
      })[] = [];

      ddlog.info(
        `Finding user submissions for ${userId} in ${courses.length} course(s)`,
      );

      for (const course of courses) {
        ddlog.info(`Checking course ${course.course.id} for userId ${userId}`);
        const exists = await getStudent(course.course.id, userId);

        // debug: get all submissions
        try {
          const allCourseSubmissions = await listCourseWorkSubmissions(
            course.course.id,
            course.courseWorkId,
          );
          ddlog.info("All coursework submissions", {
            course,
            result: allCourseSubmissions,
          });
        } catch (error: any) {
          assertNativeError(error);
          ddlog.warn(`Failed to get all course submissions. ${error.message}`);
        }

        if (!exists) continue;
        // get just the student's submissions
        const submission = await getCourseWorkUserSubmission(
          course.course.id,
          course.courseWorkId,
          userId,
        );

        if (!submission?.id) continue;
        submissions.push({
          submission,
          courseWorkId: course.courseWorkId,
          course: course.course,
        });
      }

      ddlog.info(
        `Found ${submissions.length} submissions for google classroom user`,
        {
          result: submissions,
        },
      );
      return submissions;
    };

    return {
      initialized,
      error,
      setToken,
      fetchCourses,
      fetchStudents,
      getStudent,
      createCourseWork,
      deleteCourseWork,
      turnInCourseWork,
      listCourseWorkSubmissions,
      getCourseWorkUserSubmission,
      findUserSubmissionsInExamAssignments,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.tokenServerUrl, initialized, error]);
};
